export interface Product {
  id: string
  title: string
  onlineStoreUrl: string | null
  availableForSale: boolean
  trackingParameters: string | null
  tags: string[]
  handle: string
  descriptionHtml: string
  ingredients: { value: string } | null
  compatibleDispenser: {
    value: string
  } | null
  relatedReviews: {
    value: string
  } | null
  bleedColours: {
    value: string
  } | null
  protectedStock?: {
    value: string
  } | null
  images: {
    edges: {
      node: {
        width: number
        height: number
        altText: string | null
        small: string
        medium: string
        large: string
        original: string
      }
    }[]
  }
  sellingPlanGroups: {
    edges: {
      node: {
        name: string
        sellingPlans: {
          edges: {
            node: SellingPlan
          }[]
        }
      }
    }[]
  }
  variants: {
    edges: {
      node: {
        id: string
        title: string
        image: {
          altText: string | null
          small: string
          medium: string
          large: string
          original: string
        }
        sku: string
        weight: number | null
        weightUnit: string | null
        availableForSale: boolean
        price: {
          amount: string
        }
        sellingPlanAllocations: {
          edges: {
            node: {
              sellingPlan: {
                id: string
                name: string
                priceAdjustments: {
                  adjustmentValue: {
                    adjustmentPercentage: number
                  }
                }[]
              }
            }
          }[]
        }
      }
    }[]
  }
}

export interface SellingPlan {
  id: string
  name: string
  priceAdjustments: {
    adjustmentValue: {
      adjustmentPercentage: number
    }
  }[]
}

export async function loadProduct(
  id: string,
): Promise<{ data: { product: Product } }> {
  const res = await fetch(
    `https://${import.meta.env.VITE_SHOPIFY_STORE}.myshopify.com/api/2022-10/graphql.json`,
    {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': import.meta.env
          .VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `query getProductById($id: ID!) {
          product(id: $id) {
            ${productGraphQlFragment}
          }
        }`,
        variables: {
          id: `gid://shopify/Product/${id}`,
        },
      }),
    },
  )
  return await res.json()
}

export const productGraphQlFragment = `
  id
  title
  onlineStoreUrl
  trackingParameters
  availableForSale
  compatibleDispenser: metafield(namespace: "collection_filter", key: "compatible_dispenser") {
    value
  }
  tags
  handle
  descriptionHtml
  ingredients: metafield(namespace: "product_meta", key: "ingredients_description") {
    value
  }
  relatedReviews: metafield(namespace: "custom", key: "related_product_reviews") {
    value
  }
  bleedColours: metafield(namespace: "product_meta", key: "bleed_colour") {
    value
  }
  protectedStock: metafield(namespace: "product_meta", key: "protected_stock") {
    value
  }
  images(first: 10) {
    edges {
      node {
        altText
        small: url(transform: { maxWidth: 200 })
        medium: url(transform: { maxWidth: 500 })
        large: url(transform: { maxWidth: 1000 })
        original: url
        width
        height
      }
    }
  }
  sellingPlanGroups(first: 2) {
    edges {
      node {
        name
        sellingPlans(first: 10) {
          edges {
            node {
              id
              name
              priceAdjustments {
                adjustmentValue {
                  ... on SellingPlanPercentagePriceAdjustment {
                    adjustmentPercentage
                  }
                }
              }
            }
          }

        }
      }
    }
  }
  variants(first: 10) {
    edges {
      node {
        id
        title
        sku
        image {
          altText
          small: url(transform: { maxWidth: 200 })
          medium: url(transform: { maxWidth: 500 })
          large: url(transform: { maxWidth: 1000 })
          original: url
          }
        weight
        weightUnit
        price {
          amount
        }
        availableForSale
        sellingPlanAllocations(first:10) {
          edges {
            node {
              sellingPlan {
                id
                name
                priceAdjustments {
                  adjustmentValue {
                    ... on SellingPlanPercentagePriceAdjustment {
                      adjustmentPercentage
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export async function getHandleForProduct(id: string) {
  const res = await fetch(
    `https://${
      import.meta.env.VITE_SHOPIFY_STORE
    }.myshopify.com/api/2022-10/graphql.json`,
    {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': import.meta.env
          .VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
        query Product($id: ID!) {
          product(id: $id) {
            id,
            handle
          }
        }
      `,
        variables: {
          id: `gid://shopify/Product/${id}`,
        },
      }),
    },
  )
  if (res.ok) {
    const product = (await res.json()).data.product
    return product
  } else {
    return null
  }
}

export async function getProducts(ids: string[]): Promise<Product[] | null> {
  const res = await fetch(
    `https://${
      import.meta.env.VITE_SHOPIFY_STORE
    }.myshopify.com/api/2022-10/graphql.json`,
    {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': import.meta.env
          .VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
        query Products($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              ${productGraphQlFragment}
            }
          }
        }
      `,
        variables: {
          ids: ids.map((id) => `gid://shopify/Product/${id}`),
        },
      }),
    },
  )
  if (res.ok) {
    const products = ((await res.json()).data.nodes as Product[]).filter((n) =>
      Boolean(n),
    )
    return products
  } else {
    return null
  }
}

export async function getFragranceGroupProducts(fragranceGroupId: string) {
  const res = await fetch(
    `https://${
      import.meta.env.VITE_SHOPIFY_STORE
    }.myshopify.com/api/2022-10/graphql.json`,
    {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': import.meta.env
          .VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          query FragranceGroupProducts($fragranceGroup: String!) {
            collection(handle: "all") {
              products(first: 10, filters: {
                  productMetafield: {
                    namespace: "product_meta",
                    key: "fragrance_group_id",
                    value: $fragranceGroup
                  }
                }) {
                edges {
                  node {
                    id
                    title
                    fragrance_name: metafield(namespace: "product_meta", key: "fragrance_name") {
                      value
                    }
                    images(first: 1) {
                      edges {
                        node {
                          altText
                          small: url(transform: { maxWidth: 200 })
                          medium: url(transform: { maxWidth: 500 })
                          large: url(transform: { maxWidth: 1000 })
                          original: url
                        }
                      }
                    }
                    variants(first: 10) {
                      edges {
                        node {
                          id
                          price {
                            amount
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }`,
        variables: {
          fragranceGroup: fragranceGroupId,
        },
      }),
    },
  )
  if (res.ok) {
    const collection = (await res.json()).data.collection
    if (collection) {
      const product = collection.products.edges.map(
        (e: {
          node: {
            images: { edges: [{ node: unknown }] }
            variants: { edges: { node: unknown }[] }
          }
        }) => {
          return {
            ...e.node,
            images: e.node.images.edges[0].node,
            variants: e.node.variants.edges.map((e) => e.node),
          }
        },
      )
      return product
    } else {
      return null
    }
  } else {
    return null
  }
}

export async function getProductFragranceGroup(id: string) {
  const res = await fetch(
    `https://${
      import.meta.env.VITE_SHOPIFY_STORE
    }.myshopify.com/api/2022-10/graphql.json`,
    {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': import.meta.env
          .VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
        query Product($id: ID!) {
          product(id: $id) {
            id,
            handle,
            fragrance_group_id: metafield(namespace: "product_meta", key: "fragrance_group_id") {
              value
            }
          }
        }
      `,
        variables: {
          id: `gid://shopify/Product/${id}`,
        },
      }),
    },
  )
  if (res.ok) {
    const product = (await res.json()).data.product
    return product
  } else {
    return null
  }
}

export async function getProductVariantSkus(
  ids: string[],
): Promise<{ id: string; sku: string }[] | null> {
  const res = await fetch(
    `https://${
      import.meta.env.VITE_SHOPIFY_STORE
    }.myshopify.com/api/2022-10/graphql.json`,
    {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': import.meta.env
          .VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
        query ProductVariant($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on ProductVariant {
              id
              sku
            }
          }
        }
      `,
        variables: {
          ids: ids.map((id) => `gid://shopify/ProductVariant/${id}`),
        },
      }),
    },
  )
  if (res.ok) {
    const product = (await res.json()).data.nodes
    return product
  } else {
    return null
  }
}
